import DOMController from "../abstract/DOMController";
import { triggerCustomEvent } from "../EventTrigger";
import ProductVariations from "./ProductVariations";
import AbstractProductVariationsView from "./view/AbstractProductVariationsView";
import ProductVariationsRadio from "./view/ProductVariationsRadio";

export type VariationDetails = {
    variation: any;
    groupId: number;
    caption: string;
}

export default class ProductVariationsController extends DOMController
{
    protected classes = {
        inputMarker: 'js-variation-option',
    };

    private container: Element;
    private variationsData: ProductVariations;
    private radioButtons: AbstractProductVariationsView;

    public constructor(container: Element)
    {
        super();

        this.container = container;
        this.variationsData = new ProductVariations;
        this.radioButtons = new ProductVariationsRadio;

        this.appendEventListeners();
    }

    private appendEventListeners()
    {
        this.container.addEventListener('change', e => {
            if (!this.isVariationInputEvent(e)) {
                return;
            }

            let target = e.target as HTMLInputElement;
            let identifier = this.radioButtons.getVariationIdentifier(target.name);
            this.variationChanged(identifier.groupId, identifier.optionId, Number(target.value));
            this.syncRadioButtons(identifier.groupId.toString(), identifier.optionId.toString());
        });
    }

    private isVariationInputEvent(e: Event): boolean
    {
        return (e.target as Element).classList.contains(this.classes.inputMarker);
    }

    private variationChanged(groupId: number, optionId: number, value: number)
    {
        this.variationsData.switchActiveOption(groupId, optionId, Number(value));
        this.changeVariationItemsActivity(groupId);

        triggerCustomEvent(document.body, 'product-variations-switched', {
            detail: this.createVariationDetailsObject(groupId, optionId)
        });
    }

    private createVariationDetailsObject(groupId: number, optionId: number): VariationDetails
    {
        let data = this.variationsData.getActiveVariationData(groupId);

        return {
            groupId: groupId,
            caption: data.options.values[optionId],
            variation: data,
        }
    }

    private changeVariationItemsActivity(groupId: number)
    {
        let parent = this.getGroupParentElement(groupId.toString());
        let enabledValues = this.variationsData.getEnabledVariationValues(Number(groupId));

        this.radioButtons.changeItemsActivity(parent, enabledValues);
    }

    private syncRadioButtons(groupId: string, optionId: string)
    {
        let currentValues = this.variationsData.getActiveVariationData(Number(groupId)).options.keys;
        let selector = this.radioButtons.createSelector(groupId, optionId);
        selector += `[value="${currentValues[optionId]}"]`;

        let input = document.querySelector(selector) as HTMLInputElement;
        input.checked = true;
    }

    public appendVariations(data: object)
    {
        for (let groupId in data) {
            if (!data.hasOwnProperty(groupId)) {
                continue;
            }
         
            this.variationsData.appendGroup(Number(groupId), data[groupId]);

            let activeVariation = this.variationsData.getActiveVariationData(Number(groupId));

            this.initGroupControls(groupId, activeVariation.options.keys);
            this.changeVariationItemsActivity(Number(groupId));
        }
    }

    private initGroupControls(groupId: string, activeOptionKeys: object)
    {
        let parent = this.getGroupParentElement(groupId);

        for (let optionId in activeOptionKeys) {
            if (!activeOptionKeys.hasOwnProperty(optionId)) {
                continue;
            }

            // set default active value
            this.radioButtons.set(groupId, optionId, activeOptionKeys[optionId].toString(), parent);
        }
    }

    private getGroupParentElement(groupId: string): Element
    {
        return document.getElementById(this.getHtmlIdOfGroupContainer(groupId));
    }

    public getHtmlIdOfGroupContainer(groupId: string): string
    {
        return 'group-' + groupId;
    }
}
